import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, auth, query } from '../lib/api';
import type {
  ActivityEvent,
  BlogPost,
  BlogPostDetail,
  BlogTopic,
  BulkLeadResult,
  BulkTopicsResult,
  Campaign,
  ContentBatch,
  ContentChannel,
  ContentChannelTestResult,
  Email,
  EmailTemplate,
  Job,
  JobLog,
  Lead,
  LeadType,
  Mailbox,
  MailboxTestResult,
  OAuthClientSummary,
  Paginated,
  Project,
  SearchQueryRecord,
  SessionInfo,
  StatsOverview,
  Suppression,
  User,
} from '../types/api';

/**
 * Query keys are grouped by entity so a mutation can invalidate exactly what changed
 * (`keys.leads(projectId)`) or everything under an entity (`keys.leads()`).
 */
export const keys = {
  session: ['session'] as const,
  projects: () => ['projects'] as const,
  project: (id: string) => ['project', id] as const,
  leadTypes: (projectId: string) => ['leadTypes', projectId] as const,
  leads: (projectId?: string, params?: unknown) =>
    projectId ? (['leads', projectId, params] as const) : (['leads'] as const),
  lead: (id: string) => ['lead', id] as const,
  leadStatusCounts: (projectId: string) => ['leadStatusCounts', projectId] as const,
  mailboxes: (projectId: string) => ['mailboxes', projectId] as const,
  templates: (projectId: string) => ['templates', projectId] as const,
  campaigns: (projectId?: string, params?: unknown) =>
    projectId ? (['campaigns', projectId, params] as const) : (['campaigns'] as const),
  campaign: (id: string) => ['campaign', id] as const,
  emails: (projectId?: string, params?: unknown) =>
    projectId ? (['emails', projectId, params] as const) : (['emails'] as const),
  email: (id: string) => ['email', id] as const,
  emailStatusCounts: (projectId: string) => ['emailStatusCounts', projectId] as const,
  jobs: (projectId?: string, params?: unknown) =>
    projectId ? (['jobs', projectId, params] as const) : (['jobs'] as const),
  job: (id: string) => ['job', id] as const,
  jobLogs: (id: string) => ['jobLogs', id] as const,
  stats: (projectId?: string) => ['stats', projectId ?? 'all'] as const,
  activity: (projectId?: string) => ['activity', projectId ?? 'all'] as const,
  searchQueries: (projectId: string) => ['searchQueries', projectId] as const,
  suppressions: (projectId: string) => ['suppressions', projectId] as const,
  oauthClients: ['oauthClients'] as const,
  users: ['users'] as const,
  topics: (projectId?: string, params?: unknown) =>
    projectId ? (['topics', projectId, params] as const) : (['topics'] as const),
  topic: (id: string) => ['topic', id] as const,
  topicStatusCounts: (projectId: string) => ['topicStatusCounts', projectId] as const,
  contentChannels: (projectId: string) => ['contentChannels', projectId] as const,
  contentChannel: (id: string) => ['contentChannel', id] as const,
  contentBatches: (projectId?: string, params?: unknown) =>
    projectId ? (['contentBatches', projectId, params] as const) : (['contentBatches'] as const),
  posts: (projectId?: string, params?: unknown) =>
    projectId ? (['posts', projectId, params] as const) : (['posts'] as const),
  post: (id: string) => ['post', id] as const,
  postStatusCounts: (projectId: string) => ['postStatusCounts', projectId] as const,
};

// ---------------------------------------------------------------------------
// Session, projects
// ---------------------------------------------------------------------------

export const useSession = () =>
  useQuery({
    queryKey: keys.session,
    queryFn: () => api.get<SessionInfo>('/auth/me'),
    staleTime: 60_000,
  });

/** True when the signed-in principal may administer users and MCP clients. */
export const useIsAdmin = (): boolean => {
  const { data } = useSession();
  return Boolean(data?.scopes.includes('admin'));
};

export const useUsers = (enabled = true) =>
  useQuery({
    queryKey: keys.users,
    queryFn: () => api.get<User[]>('/users'),
    enabled,
  });

export const useCreateUser = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; email: string; password: string; role: string }) =>
      api.post<User>('/users', body),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.users }),
  });
};

export const useUpdateUser = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Partial<Pick<User, 'name' | 'email' | 'role' | 'isActive'>>) =>
      api.patch<User>(`/users/${id}`, body),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.users }),
  });
};

export const useSetUserPassword = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) =>
      api.post<User>(`/users/${id}/password`, { password, mustChangePassword: true }),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.users }),
  });
};

export const useDeleteUser = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/users/${id}`),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.users }),
  });
};

/** Changing your own password rotates the session, so the new token replaces the stored one. */
export const useChangePassword = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: { currentPassword: string; newPassword: string }) =>
      api.post<{ accessToken: string }>('/auth/change-password', body),
    onSuccess: (result) => {
      if (result?.accessToken) auth.set(result.accessToken);
      void client.invalidateQueries({ queryKey: keys.session });
      void client.invalidateQueries({ queryKey: keys.users });
    },
  });
};

export const useUpdateProfile = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string }) => api.patch<User>('/auth/me', body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.session });
      void client.invalidateQueries({ queryKey: keys.users });
    },
  });
};

export const useProjects = () =>
  useQuery({ queryKey: keys.projects(), queryFn: () => api.get<Project[]>('/projects') });

export const useProject = (id: string | undefined) =>
  useQuery({
    queryKey: keys.project(id ?? ''),
    queryFn: () => api.get<Project>(`/projects/${id}`),
    enabled: Boolean(id),
  });

export const useCreateProject = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<Project>) => api.post<Project>('/projects', body),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.projects() }),
  });
};

export const useUpdateProject = (id: string) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<Project>) => api.patch<Project>(`/projects/${id}`, body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.project(id) });
      void client.invalidateQueries({ queryKey: keys.projects() });
    },
  });
};

export const useDeleteProject = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/projects/${id}`),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.projects() }),
  });
};

// ---------------------------------------------------------------------------
// Lead types
// ---------------------------------------------------------------------------

export const useLeadTypes = (projectId: string | undefined) =>
  useQuery({
    queryKey: keys.leadTypes(projectId ?? ''),
    queryFn: () => api.get<LeadType[]>(`/projects/${projectId}/lead-types`),
    enabled: Boolean(projectId),
  });

export const useSaveLeadType = (projectId: string) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Partial<LeadType> & { id?: string }) =>
      id
        ? api.patch<LeadType>(`/lead-types/${id}`, body)
        : api.post<LeadType>(`/projects/${projectId}/lead-types`, body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.leadTypes(projectId) });
      void client.invalidateQueries({ queryKey: keys.project(projectId) });
    },
  });
};

export const useDeleteLeadType = (projectId: string) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/lead-types/${id}`),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.leadTypes(projectId) }),
  });
};

// ---------------------------------------------------------------------------
// Leads
// ---------------------------------------------------------------------------

export interface LeadFilters {
  page: number;
  pageSize: number;
  search?: string;
  status?: string;
  leadTypeId?: string;
  country?: string;
  hasEmail?: boolean;
  tag?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export const useLeads = (projectId: string | undefined, filters: LeadFilters) =>
  useQuery({
    queryKey: keys.leads(projectId, filters),
    queryFn: () => api.get<Paginated<Lead>>(`/projects/${projectId}/leads${query({ ...filters })}`),
    enabled: Boolean(projectId),
    placeholderData: (previous) => previous,
  });

export const useLeadStatusCounts = (projectId: string | undefined) =>
  useQuery({
    queryKey: keys.leadStatusCounts(projectId ?? ''),
    queryFn: () => api.get<Record<string, number>>(`/projects/${projectId}/leads/status-counts`),
    enabled: Boolean(projectId),
  });

export const useLead = (id: string | undefined) =>
  useQuery({
    queryKey: keys.lead(id ?? ''),
    queryFn: () => api.get<Lead>(`/leads/${id}`),
    enabled: Boolean(id),
  });

const invalidateLeadViews = (client: ReturnType<typeof useQueryClient>, projectId: string) => {
  void client.invalidateQueries({ queryKey: keys.leads() });
  void client.invalidateQueries({ queryKey: keys.leadStatusCounts(projectId) });
  void client.invalidateQueries({ queryKey: keys.leadTypes(projectId) });
  void client.invalidateQueries({ queryKey: keys.stats(projectId) });
};

export const useSaveLead = (projectId: string) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Partial<Lead> & { id?: string }) =>
      id ? api.patch<Lead>(`/leads/${id}`, body) : api.post<Lead>(`/projects/${projectId}/leads`, body),
    onSuccess: (lead) => {
      invalidateLeadViews(client, projectId);
      if (lead?.id) void client.invalidateQueries({ queryKey: keys.lead(lead.id) });
    },
  });
};

export const useDeleteLead = (projectId: string) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/leads/${id}`),
    onSuccess: () => invalidateLeadViews(client, projectId),
  });
};

export const useBulkLeadAction = (projectId: string) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: { leadIds: string[]; status?: string; leadTypeId?: string; addTags?: string }) =>
      api.post<{ updated: number }>(`/projects/${projectId}/leads/bulk-action`, body),
    onSuccess: () => invalidateLeadViews(client, projectId),
  });
};

export const useBulkDeleteLeads = (projectId: string) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (leadIds: string[]) =>
      api.post<{ deleted: number }>(`/projects/${projectId}/leads/bulk-delete`, { leadIds }),
    onSuccess: () => invalidateLeadViews(client, projectId),
  });
};

export const useImportLeads = (projectId: string) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: { csv: string; leadTypeId?: string; source?: string }) =>
      api.post<BulkLeadResult>(`/projects/${projectId}/leads/import`, body),
    onSuccess: () => invalidateLeadViews(client, projectId),
  });
};

// ---------------------------------------------------------------------------
// Mailboxes
// ---------------------------------------------------------------------------

export const useMailboxes = (projectId: string | undefined) =>
  useQuery({
    queryKey: keys.mailboxes(projectId ?? ''),
    queryFn: () => api.get<Mailbox[]>(`/projects/${projectId}/mailboxes`),
    enabled: Boolean(projectId),
  });

export const useSaveMailbox = (projectId: string) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Record<string, unknown> & { id?: string }) =>
      id
        ? api.patch<Mailbox>(`/mailboxes/${id}`, body)
        : api.post<Mailbox>(`/projects/${projectId}/mailboxes`, body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.mailboxes(projectId) });
      void client.invalidateQueries({ queryKey: keys.project(projectId) });
    },
  });
};

export const useDeleteMailbox = (projectId: string) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/mailboxes/${id}`),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.mailboxes(projectId) }),
  });
};

export const useTestMailbox = () =>
  useMutation({
    mutationFn: ({ id, sendTestTo }: { id: string; sendTestTo?: string }) =>
      api.post<MailboxTestResult>(`/mailboxes/${id}/test`, sendTestTo ? { sendTestTo } : {}),
  });

export const useSyncMailbox = (projectId: string) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<unknown>(`/mailboxes/${id}/sync`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.mailboxes(projectId) });
      void client.invalidateQueries({ queryKey: keys.emails() });
    },
  });
};

// ---------------------------------------------------------------------------
// Templates
// ---------------------------------------------------------------------------

export const useTemplates = (projectId: string | undefined) =>
  useQuery({
    queryKey: keys.templates(projectId ?? ''),
    queryFn: () => api.get<EmailTemplate[]>(`/projects/${projectId}/templates`),
    enabled: Boolean(projectId),
  });

export const useSaveTemplate = (projectId: string) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Partial<EmailTemplate> & { id?: string }) =>
      id
        ? api.patch<EmailTemplate>(`/templates/${id}`, body)
        : api.post<EmailTemplate>(`/projects/${projectId}/templates`, body),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.templates(projectId) }),
  });
};

export const useDeleteTemplate = (projectId: string) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/templates/${id}`),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.templates(projectId) }),
  });
};

// ---------------------------------------------------------------------------
// Campaigns
// ---------------------------------------------------------------------------

export const useCampaigns = (projectId: string | undefined, params: { status?: string } = {}) =>
  useQuery({
    queryKey: keys.campaigns(projectId, params),
    queryFn: () =>
      api.get<Paginated<Campaign>>(`/projects/${projectId}/campaigns${query({ ...params, pageSize: 100 })}`),
    enabled: Boolean(projectId),
  });

export const useCampaign = (id: string | undefined) =>
  useQuery({
    queryKey: keys.campaign(id ?? ''),
    queryFn: () => api.get<Campaign>(`/campaigns/${id}`),
    enabled: Boolean(id),
  });

export const useCreateCampaign = (projectId: string) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.post<Campaign>(`/projects/${projectId}/campaigns`, body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.campaigns() });
      void client.invalidateQueries({ queryKey: keys.jobs() });
      invalidateLeadViews(client, projectId);
    },
  });
};

export const useUpdateCampaign = (projectId: string) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Record<string, unknown> & { id: string }) =>
      api.patch<Campaign>(`/campaigns/${id}`, body),
    onSuccess: (_data, variables) => {
      void client.invalidateQueries({ queryKey: keys.campaigns() });
      void client.invalidateQueries({ queryKey: keys.campaign(variables.id) });
      void client.invalidateQueries({ queryKey: keys.emails() });
      void client.invalidateQueries({ queryKey: keys.stats(projectId) });
    },
  });
};

export const useDeleteCampaign = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/campaigns/${id}`),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.campaigns() }),
  });
};

// ---------------------------------------------------------------------------
// Emails
// ---------------------------------------------------------------------------

export interface EmailFilters {
  page: number;
  pageSize: number;
  status?: string;
  direction?: string;
  campaignId?: string;
  search?: string;
}

export const useEmails = (projectId: string | undefined, filters: EmailFilters) =>
  useQuery({
    queryKey: keys.emails(projectId, filters),
    queryFn: () => api.get<Paginated<Email>>(`/projects/${projectId}/emails${query({ ...filters })}`),
    enabled: Boolean(projectId),
    placeholderData: (previous) => previous,
  });

export const useEmail = (id: string | undefined) =>
  useQuery({
    queryKey: keys.email(id ?? ''),
    queryFn: () => api.get<Email>(`/emails/${id}`),
    enabled: Boolean(id),
  });

export const useEmailStatusCounts = (projectId: string | undefined) =>
  useQuery({
    queryKey: keys.emailStatusCounts(projectId ?? ''),
    queryFn: () => api.get<Record<string, number>>(`/projects/${projectId}/emails/status-counts`),
    enabled: Boolean(projectId),
  });

const invalidateEmailViews = (client: ReturnType<typeof useQueryClient>, projectId: string) => {
  void client.invalidateQueries({ queryKey: keys.emails() });
  void client.invalidateQueries({ queryKey: keys.emailStatusCounts(projectId) });
  void client.invalidateQueries({ queryKey: keys.stats(projectId) });
  void client.invalidateQueries({ queryKey: keys.leads() });
};

export const useEmailAction = (projectId: string) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      action,
      body,
    }: {
      id: string;
      action: 'approve' | 'send' | 'cancel';
      body?: Record<string, unknown>;
    }) => api.post<Email>(`/emails/${id}/${action}`, body ?? {}),
    onSuccess: (_data, variables) => {
      invalidateEmailViews(client, projectId);
      void client.invalidateQueries({ queryKey: keys.email(variables.id) });
    },
  });
};

export const useSaveEmail = (projectId: string) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Record<string, unknown> & { id?: string }) =>
      id
        ? api.patch<Email>(`/emails/${id}`, body)
        : api.post<Email>(`/projects/${projectId}/emails`, body),
    onSuccess: (email) => {
      invalidateEmailViews(client, projectId);
      if (email?.id) void client.invalidateQueries({ queryKey: keys.email(email.id) });
    },
  });
};

export const useDeleteEmail = (projectId: string) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/emails/${id}`),
    onSuccess: () => invalidateEmailViews(client, projectId),
  });
};

export const useBulkEmailAction = (projectId: string) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: { emailIds: string[]; status: string; scheduledAt?: string }) =>
      api.post<{ updated: number }>(`/projects/${projectId}/emails/bulk-action`, body),
    onSuccess: () => invalidateEmailViews(client, projectId),
  });
};

// ---------------------------------------------------------------------------
// Jobs
// ---------------------------------------------------------------------------

export const useJobs = (
  projectId: string | undefined,
  params: { status?: string; type?: string; page?: number; pageSize?: number } = {},
  refetchInterval?: number,
) =>
  useQuery({
    queryKey: keys.jobs(projectId, params),
    queryFn: () =>
      api.get<Paginated<Job>>(`/projects/${projectId}/jobs${query({ ...params, pageSize: params.pageSize ?? 50 })}`),
    enabled: Boolean(projectId),
    refetchInterval,
  });

export const useJob = (id: string | undefined, refetchInterval?: number) =>
  useQuery({
    queryKey: keys.job(id ?? ''),
    queryFn: () => api.get<Job>(`/jobs/${id}`),
    enabled: Boolean(id),
    refetchInterval,
  });

export const useJobLogs = (id: string | undefined, refetchInterval?: number) =>
  useQuery({
    queryKey: keys.jobLogs(id ?? ''),
    queryFn: () => api.get<JobLog[]>(`/jobs/${id}/logs`),
    enabled: Boolean(id),
    refetchInterval,
  });

export const useCreateJob = (projectId: string) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post<Job>(`/projects/${projectId}/jobs`, body),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.jobs() }),
  });
};

export const useJobAction = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'cancel' | 'retry' }) =>
      api.post<Job>(`/jobs/${id}/${action}`),
    onSuccess: (_data, variables) => {
      void client.invalidateQueries({ queryKey: keys.jobs() });
      void client.invalidateQueries({ queryKey: keys.job(variables.id) });
    },
  });
};

export const useDeleteJob = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/jobs/${id}`),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.jobs() }),
  });
};

// ---------------------------------------------------------------------------
// Stats, activity, research, suppression, MCP clients
// ---------------------------------------------------------------------------

export const useStats = (projectId: string | undefined, refetchInterval?: number) =>
  useQuery({
    queryKey: keys.stats(projectId),
    queryFn: () => api.get<StatsOverview>(`/stats/overview${query({ projectId })}`),
    refetchInterval,
  });

export const useActivity = (projectId: string | undefined, limit = 40) =>
  useQuery({
    queryKey: keys.activity(projectId),
    queryFn: () => api.get<ActivityEvent[]>(`/activity${query({ projectId, limit })}`),
  });

export const useSearchQueries = (projectId: string | undefined) =>
  useQuery({
    queryKey: keys.searchQueries(projectId ?? ''),
    queryFn: () => api.get<SearchQueryRecord[]>(`/projects/${projectId}/search-queries`),
    enabled: Boolean(projectId),
  });

export const useSuppressions = (projectId: string | undefined) =>
  useQuery({
    queryKey: keys.suppressions(projectId ?? ''),
    queryFn: () => api.get<Suppression[]>(`/projects/${projectId}/suppressions`),
    enabled: Boolean(projectId),
  });

export const useAddSuppression = (projectId: string) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: { type: string; value: string; reason?: string }) =>
      api.post<Suppression>(`/projects/${projectId}/suppressions`, body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.suppressions(projectId) });
      invalidateLeadViews(client, projectId);
    },
  });
};

export const useDeleteSuppression = (projectId: string) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/suppressions/${id}`),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.suppressions(projectId) }),
  });
};

export const useOAuthClients = (enabled = true) =>
  useQuery({
    queryKey: keys.oauthClients,
    queryFn: () => api.get<OAuthClientSummary[]>('/oauth-clients'),
    enabled,
  });

export const useCreateOAuthClient = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string }) =>
      api.post<OAuthClientSummary & { clientSecret: string }>('/oauth-clients', body),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.oauthClients }),
  });
};

export const useOAuthClientAction = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'disable' | 'enable' }) =>
      api.post<OAuthClientSummary>(`/oauth-clients/${id}/${action}`),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.oauthClients }),
  });
};

export const useDeleteOAuthClient = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/oauth-clients/${id}`),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.oauthClients }),
  });
};

export const useRunWorker = () =>
  useMutation({
    mutationFn: (worker: 'send' | 'inbox' | 'janitor') => api.post<unknown>(`/workers/${worker}/run`),
  });

// ---------------------------------------------------------------------------
// Content topics
// ---------------------------------------------------------------------------

export interface TopicFilters {
  page: number;
  pageSize: number;
  search?: string;
  status?: string;
  language?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export interface TopicInput {
  title: string;
  angle?: string;
  keywords?: string;
  language?: string;
  priority?: number;
  notes?: string;
}

export const useTopics = (projectId: string | undefined, filters: TopicFilters) =>
  useQuery({
    queryKey: keys.topics(projectId, filters),
    queryFn: () =>
      api.get<Paginated<BlogTopic>>(`/projects/${projectId}/topics${query({ ...filters })}`),
    enabled: Boolean(projectId),
    placeholderData: (previous) => previous,
  });

export const useTopicStatusCounts = (projectId: string | undefined) =>
  useQuery({
    queryKey: keys.topicStatusCounts(projectId ?? ''),
    queryFn: () => api.get<Record<string, number>>(`/projects/${projectId}/topics/status-counts`),
    enabled: Boolean(projectId),
  });

const invalidateTopicViews = (client: ReturnType<typeof useQueryClient>, projectId: string) => {
  void client.invalidateQueries({ queryKey: keys.topics() });
  void client.invalidateQueries({ queryKey: keys.topicStatusCounts(projectId) });
};

export const useSaveTopic = (projectId: string) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Partial<BlogTopic> & { id?: string }) =>
      id
        ? api.patch<BlogTopic>(`/topics/${id}`, body)
        : api.post<BlogTopic>(`/projects/${projectId}/topics`, body),
    onSuccess: (topic) => {
      invalidateTopicViews(client, projectId);
      if (topic?.id) void client.invalidateQueries({ queryKey: keys.topic(topic.id) });
    },
  });
};

export const useUpdateTopic = (projectId: string) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Partial<BlogTopic> & { id: string }) =>
      api.patch<BlogTopic>(`/topics/${id}`, body),
    onSuccess: (topic) => {
      invalidateTopicViews(client, projectId);
      if (topic?.id) void client.invalidateQueries({ queryKey: keys.topic(topic.id) });
    },
  });
};

export const useBulkTopics = (projectId: string) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: { topics: TopicInput[]; language?: string }) =>
      api.post<BulkTopicsResult>(`/projects/${projectId}/topics/bulk`, body),
    onSuccess: () => invalidateTopicViews(client, projectId),
  });
};

export const useImportTopics = (projectId: string) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: { text: string; language?: string }) =>
      api.post<BulkTopicsResult>(`/projects/${projectId}/topics/import`, body),
    onSuccess: () => invalidateTopicViews(client, projectId),
  });
};

export const useDeleteTopic = (projectId: string) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ deleted: number }>(`/topics/${id}`),
    onSuccess: () => invalidateTopicViews(client, projectId),
  });
};

export const useBulkDeleteTopics = (projectId: string) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (topicIds: string[]) =>
      api.post<{ deleted: number }>(`/projects/${projectId}/topics/bulk-delete`, { topicIds }),
    onSuccess: () => invalidateTopicViews(client, projectId),
  });
};

// ---------------------------------------------------------------------------
// Content channels (publishing targets)
// ---------------------------------------------------------------------------

export const useContentChannels = (projectId: string | undefined) =>
  useQuery({
    queryKey: keys.contentChannels(projectId ?? ''),
    queryFn: () => api.get<ContentChannel[]>(`/projects/${projectId}/content-channels`),
    enabled: Boolean(projectId),
  });

export const useContentChannel = (id: string | undefined) =>
  useQuery({
    queryKey: keys.contentChannel(id ?? ''),
    queryFn: () => api.get<ContentChannel>(`/content-channels/${id}`),
    enabled: Boolean(id),
  });

export const useSaveContentChannel = (projectId: string) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Record<string, unknown> & { id?: string }) =>
      id
        ? api.patch<ContentChannel>(`/content-channels/${id}`, body)
        : api.post<ContentChannel>(`/projects/${projectId}/content-channels`, body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.contentChannels(projectId) });
      void client.invalidateQueries({ queryKey: keys.project(projectId) });
    },
  });
};

export const useDeleteContentChannel = (projectId: string) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ deleted: boolean; label: string }>(`/content-channels/${id}`),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.contentChannels(projectId) }),
  });
};

export const useTestContentChannel = () =>
  useMutation({
    mutationFn: (id: string) => api.post<ContentChannelTestResult>(`/content-channels/${id}/test`),
  });

// ---------------------------------------------------------------------------
// Content batches ("Queue content")
// ---------------------------------------------------------------------------

export const useCreateContentBatch = (projectId: string) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.post<ContentBatch>(`/projects/${projectId}/content/batches`, body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.contentBatches() });
      void client.invalidateQueries({ queryKey: keys.jobs() });
      invalidateTopicViews(client, projectId);
    },
  });
};

// ---------------------------------------------------------------------------
// Blog posts
// ---------------------------------------------------------------------------

export interface PostFilters {
  page: number;
  pageSize: number;
  status?: string;
  search?: string;
  channelId?: string;
  batchId?: string;
  topicId?: string;
}

export const useBlogPosts = (projectId: string | undefined, filters: PostFilters) =>
  useQuery({
    queryKey: keys.posts(projectId, filters),
    queryFn: () => api.get<Paginated<BlogPost>>(`/projects/${projectId}/posts${query({ ...filters })}`),
    enabled: Boolean(projectId),
    placeholderData: (previous) => previous,
  });

export const useBlogPostStatusCounts = (projectId: string | undefined) =>
  useQuery({
    queryKey: keys.postStatusCounts(projectId ?? ''),
    queryFn: () => api.get<Record<string, number>>(`/projects/${projectId}/posts/status-counts`),
    enabled: Boolean(projectId),
  });

export const useBlogPost = (id: string | undefined) =>
  useQuery({
    queryKey: keys.post(id ?? ''),
    queryFn: () => api.get<BlogPostDetail>(`/posts/${id}`),
    enabled: Boolean(id),
  });

const invalidatePostViews = (client: ReturnType<typeof useQueryClient>, projectId: string) => {
  void client.invalidateQueries({ queryKey: keys.posts() });
  void client.invalidateQueries({ queryKey: keys.postStatusCounts(projectId) });
  void client.invalidateQueries({ queryKey: keys.topics() });
  void client.invalidateQueries({ queryKey: keys.stats(projectId) });
};

export const useUpdateBlogPost = (projectId: string) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Record<string, unknown> & { id: string }) =>
      api.patch<BlogPost>(`/posts/${id}`, body),
    onSuccess: (_data, variables) => {
      invalidatePostViews(client, projectId);
      void client.invalidateQueries({ queryKey: keys.post(variables.id) });
    },
  });
};

export const useApproveBlogPost = (projectId: string) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<BlogPost>(`/posts/${id}/approve`),
    onSuccess: (_data, id) => {
      invalidatePostViews(client, projectId);
      void client.invalidateQueries({ queryKey: keys.post(id) });
    },
  });
};

export const useBulkApproveBlogPosts = (projectId: string) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => api.post<{ approved: number; skipped: number; approvedIds: string[] }>(
      `/projects/${projectId}/posts/bulk-approve`, { ids },
    ),
    onSuccess: (_data, ids) => {
      invalidatePostViews(client, projectId);
      ids.forEach((id) => void client.invalidateQueries({ queryKey: keys.post(id) }));
      void client.invalidateQueries({ queryKey: keys.activity(projectId) });
    },
  });
};

export const usePublishBlogPost = (projectId: string) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ status: string; externalUrl?: string; error?: string }>(`/posts/${id}/publish`),
    onSuccess: (_data, id) => {
      invalidatePostViews(client, projectId);
      void client.invalidateQueries({ queryKey: keys.post(id) });
    },
  });
};

export const useCancelBlogPost = (projectId: string) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<BlogPost>(`/posts/${id}/cancel`),
    onSuccess: (_data, id) => {
      invalidatePostViews(client, projectId);
      void client.invalidateQueries({ queryKey: keys.post(id) });
    },
  });
};

export const useDeleteBlogPost = (projectId: string) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ deleted: number }>(`/posts/${id}`),
    onSuccess: () => invalidatePostViews(client, projectId),
  });
};

export const useScheduleEdit = (projectId: string) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ postId, ...body }: { postId: string } & Record<string, unknown>) =>
      api.post<{ batchId: string; taskCount: number; jobId: string }>(
        `/projects/${projectId}/posts/${postId}/edit`,
        body,
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.contentBatches() });
      void client.invalidateQueries({ queryKey: keys.jobs() });
      invalidatePostViews(client, projectId);
    },
  });
};
